/**
 * 注文書4構成 (注文情報 / 集荷情報 / 納品情報 / 注文明細) のフィールド割当。
 *
 * フラットな Order DTO の各フィールドを、ちょうど 1 つの区分に割り当てる単一源泉。
 * admin の 4 節アコーディオン・取込プレビュー・詳細表示が同じ区分を共有する。
 *
 * 物理 DB は orders(ヘッダ) + order_locations(kind 統合) + order_delivery_info(納品固有) +
 * order_items に正規化されるが、API は Order DTO をフラット維持し repository が組み立てる
 * (DB のネスト構造を API に波及させない)。網羅性・排他性は order-sections.test.ts で
 * Order スキーマと突合して CI ゲート化する。
 */
export const ORDER_SECTION_FIELDS = {
  // 注文情報: orders ヘッダ行
  orderInfo: [
    "order_number",
    "client_id",
    "payment_number",
    "payment_type",
    "status",
    "source_type",
    "parse_method",
    "remarks",
    // #767: 積載時間の上限 (標準 / 延長) は案件ごとの設定なので orders ヘッダ = 注文情報。
    "max_ride_minutes",
    "uploaded_file_public_id",
  ],
  // 集荷情報: order_locations WHERE kind IN ('pickup','waypoint')
  // 経由地 (#722) は集荷側の立寄り地点なので集荷情報に属させる。
  pickup: [
    "pickup_spot_public_id",
    "pickup_sequence",
    "waypoints",
    "store_name",
    "store_address",
    "store_phone",
    "pickup_time_start",
    "pickup_time_end",
    "pickup_unavailable_times",
    "pickup_duration_minutes",
    "store_message",
    "pickup_latitude",
    "pickup_longitude",
  ],
  // 納品情報: order_locations WHERE kind='delivery' + order_delivery_info
  delivery: [
    "delivery_spot_public_id",
    "delivery_address",
    "delivery_date",
    "delivery_time_start",
    "delivery_time_end",
    "delivery_building",
    "handover_location",
    "arrival_instructions",
    "delivery_company",
    "delivery_branch",
    "delivery_person_name",
    "delivery_person_name_kana",
    "delivery_email",
    "delivery_phone",
    "delivery_packaging",
    "delivery_notes",
    "day_of_contact_person",
    "day_of_contact_phone",
    "delivery_duration_minutes",
    "delivery_unavailable_times",
    "delivery_latitude",
    "delivery_longitude",
    "delivery_coordinates_resolved",
  ],
  // 注文明細: order_items
  items: ["items"],
} as const;

/**
 * 4 構成に属さないメタフィールド (システム管理項目)。
 * `client_name` は clients join で解決する読み取り専用の表示用フィールド (保存入力には含まない)。
 */
export const ORDER_META_FIELDS = [
  "id",
  "client_name",
  "on_locked_route",
  "created_at",
  "updated_at",
] as const;

export type OrderSection = keyof typeof ORDER_SECTION_FIELDS;
export type OrderSectionField = (typeof ORDER_SECTION_FIELDS)[OrderSection][number];

/** 全区分のフィールドを平坦に結合した配列 (orderInfo → pickup → delivery → items の順)。 */
export const ALL_ORDER_SECTION_FIELDS = [
  ...ORDER_SECTION_FIELDS.orderInfo,
  ...ORDER_SECTION_FIELDS.pickup,
  ...ORDER_SECTION_FIELDS.delivery,
  ...ORDER_SECTION_FIELDS.items,
] as const;
